import {
  annotationsActions,
  annotationsSelectors,
  applicationActions,
  applicationSelectors,
  authSelectors,
  readerActions,
  readerSelectors,
  searchActions,
  useAppDispatch,
  usersSelectors,
} from '@readcloud/state';
import C from 'classnames';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { DocumentWithExtrasContext } from '../../context/DocumentWithExtras';
import { TextSelectionContext } from '../../context/TextSelection';
import { getType } from '../../constants';
import { ConnectedExternalUrlModal } from '../Modals/ExternalUrl/ConnectedExternalUrlModal';
import { TopToolbar } from './TopToolbar/TopToolbar';

interface IConnectedTopToolbarProps {
  className?: string;
  mode?: 'lti';
}

const ConnectedTopToolbar: React.FunctionComponent<
  IConnectedTopToolbarProps
> = (props) => {
  const readerData = useSelector(readerSelectors.getReaderData);
  const appData = useSelector(applicationSelectors.getApplcationState);
  const currentlyDrawing = useSelector(
    annotationsSelectors.getCurrentlyDrawing
  );
  const currentTool = useSelector(annotationsSelectors.getCurrentTool);
  const user = useSelector(authSelectors.getUser);
  const knownUsers = useSelector(usersSelectors.getKnownUsers);

  const { location, loading, type } = React.useContext(
    DocumentWithExtrasContext
  );
  const dispatch = useAppDispatch();

  const [externalModalOpen, setExternalModalOpen] =
    React.useState<boolean>(false);

  const { customTextSelection } = React.useContext(TextSelectionContext);
  return (
    <>
      <TopToolbar
        isOpen={appData.toolbarsOpen || type === 'PLAYER'}
        className={C(props.className, 'toolbar top-toolbar')}
        authorId={user.id}
        bookId={readerData.bookId}
        addAnnotation={(anno) =>
          dispatch(annotationsActions.asyncActions.addAnnotation(anno))
        }
        onDiscardDrawing={() =>
          dispatch(annotationsActions.updateCurrentlyDrawing(undefined))
        }
        pageNumber={parseInt(location)}
        onToolChange={(tool) =>
          dispatch(annotationsActions.updateCurrentTool(tool))
        }
        tool={currentTool}
        disableAnnotations={getType(readerData.fileUrl) === 'UNKNOWN'}
        disableSearch={
          !(
            getType(readerData.fileUrl) === 'PDF' ||
            getType(readerData.fileUrl) === 'EPUB'
          )
        }
        disableBookmarks={
          !(
            getType(readerData.fileUrl) === 'PDF' ||
            getType(readerData.fileUrl) === 'EPUB'
          )
        }
        disableDraw={
          !(
            getType(readerData.fileUrl) === 'PDF' ||
            getType(readerData.fileUrl) === 'EPUB'
          ) ||
          !(readerData.version === '3') ||
          user?.permissions?.indexOf('Drawing') === -1
        }
        disableHTML={
          !(
            getType(readerData.fileUrl) === 'PDF' ||
            getType(readerData.fileUrl) === 'EPUB'
          ) ||
          !(readerData.version === '3') ||
          user?.permissions?.indexOf('HTML') === -1
        }
        disableMedia={
          !(
            getType(readerData.fileUrl) === 'PDF' ||
            getType(readerData.fileUrl) === 'EPUB'
          ) ||
          !(readerData.version === '3') ||
          user?.permissions?.indexOf('Media') === -1
        }
        searchPanelClassName="searchPanel"
        externalUrlButtonClassName="externalUrl" //{steps.externalUrl.className}
        title={readerData.title || 'Unknown'}
        goHome={undefined} //bridgeContainer.bridgeHome
        {...(props.mode === 'lti'
          ? {
              homeButtonIcon: <i className="fe fe-chevron-left" />,
              tooltip: 'Back',
            }
          : {})}
        onAnnotations={() =>
          dispatch(
            applicationActions.updateApplication({
              annotationsSidebarOpen: true,
            })
          )
        }
        onBookmarks={() =>
          dispatch(
            applicationActions.updateApplication({ bookmarksSidebarOpen: true })
          )
        }
        onSearchOpen={() => dispatch(searchActions.setOpen(true))}
        onPrint={
          readerData.printRights?.printablePages ||
          readerData.bookOptions.printable
            ? () =>
                dispatch(readerActions.updateReaderData({ printMode: true }))
            : undefined
        }
        onLink={() =>
          readerData.bookId && readerData.cloudId && user
            ? setExternalModalOpen(true)
            : null
        }
        currentlyDrawing={
          getType(readerData.fileUrl) === 'PDF' ? currentlyDrawing : undefined
        }
        onBeginDrawing={
          getType(readerData.fileUrl) === 'PDF'
            ? (pageNumber: number) =>
                dispatch(annotationsActions.updateCurrentlyDrawing(pageNumber))
            : undefined
        }
        currentlyFilteringText={readerData?.viewState?.annotationFilter}
        saveAnnotationFilter={(filter) =>
          dispatch(
            readerActions.asyncActions.updateViewState({
              annotationFilter: filter,
            })
          )
        }
        currentAuthor={user.id}
        filterUsers={readerData?.viewState?.annotationFilterUsers}
        setUserFilter={(filter) => {
          dispatch(
            readerActions.asyncActions.updateViewState({
              annotationFilterUsers: filter,
            })
          );
        }}
        filterAnnotationBy={(filter, users) =>
          dispatch(
            annotationsActions.asyncActions.setAnnotationFilter({
              types: filter,
              users,
            })
          )
        }
        knownUsers={knownUsers}
        disableImmersive={!(user?.permissions?.indexOf('Immersive') !== -1)}
        disableLink={!readerData.bookId || !readerData.cloudId || !user}
        getImmersiveToken={() =>
          dispatch(applicationActions.asyncActions.getImmersiveToken())
        }
        isOnline={appData.online}
        checkOnline={() => true} //appContainer.checkOnlineStatus -- this always returns true at the moment.
        toggleIOSInterceptor={(status) =>
          dispatch(
            applicationActions.asyncActions.updateNativeInterceptor(status)
          )
        }
        printIOS={(html) => {
          // TODO: Implement printing functionality
        }}
        loadingPages={loading}
        customTextSelection={customTextSelection}
      />
      <ConnectedExternalUrlModal
        brand={user.clientSmallBrand}
        bookId={readerData.bookId}
        cloudId={readerData.cloudId}
        location={readerData?.viewState?.location}
        isOpen={externalModalOpen}
        toggle={() => setExternalModalOpen((s) => !s)}
      />
    </>
  );
};

export default ConnectedTopToolbar;
